import React, { useEffect, useMemo, useState } from 'react';
import { useAuth } from '../context/AuthContext';
import './AdminUsersManager.css';

function AdminUsersManager() {
  const { fetchWithAuth, isAdmin } = useAuth();
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [users, setUsers] = useState([]);
  const [total, setTotal] = useState(0);
  const [query, setQuery] = useState('');
  const [role, setRole] = useState('all');
  const [sortBy, setSortBy] = useState('createdAt');
  const [order, setOrder] = useState('desc');
  const [notifyTarget, setNotifyTarget] = useState(null);
  const [notifyMessage, setNotifyMessage] = useState('');

  const canManage = isAdmin;

  const loadUsers = async () => {
    try {
      setLoading(true);
      setError('');
      const params = new URLSearchParams({ query, role, sortBy, order, limit: '200', offset: '0' });
      const res = await fetchWithAuth(`/api/admin/users?${params.toString()}`);
      if (!res.ok) {
        const errText = await res.text();
        throw new Error(errText || `Fehler ${res.status}`);
      }
      const data = await res.json();
      setUsers(data.items || []);
      setTotal(data.total || 0);
    } catch (e) {
      setError(e.message);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadUsers();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [query, role, sortBy, order]);

  const handleDelete = async (id) => {
    if (!window.confirm('User wirklich löschen? Dieser Vorgang kann nicht rückgängig gemacht werden.')) return;
    try {
      const res = await fetchWithAuth(`/api/admin/users/${id}`, { method: 'DELETE' });
      if (!res.ok) {
        const err = await res.json().catch(() => ({ message: res.statusText }));
        throw new Error(err.message || 'Löschen fehlgeschlagen');
      }
      await loadUsers();
    } catch (e) {
      setError(e.message);
    }
  };

  const handleRoleChange = async (id, role) => {
    try {
      const res = await fetchWithAuth(`/api/admin/users/${id}/role`, {
        method: 'PATCH',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ role })
      });
      if (!res.ok) {
        const err = await res.json().catch(() => ({ message: res.statusText }));
        throw new Error(err.message || 'Rollen-Update fehlgeschlagen');
      }
      await loadUsers();
    } catch (e) {
      setError(e.message);
    }
  };

  const openNotify = (user) => {
    setNotifyTarget(user);
    setNotifyMessage('');
  };

  const sendNotify = async () => {
    if (!notifyTarget || !notifyMessage.trim()) return;
    try {
      const res = await fetchWithAuth(`/api/admin/users/${notifyTarget._id}/notify`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ message: notifyMessage })
      });
      if (!res.ok) {
        const err = await res.json().catch(() => ({ message: res.statusText }));
        throw new Error(err.message || 'Benachrichtigung fehlgeschlagen');
      }
      setNotifyTarget(null);
      setNotifyMessage('');
    } catch (e) {
      setError(e.message);
    }
  };

  const sortedColumns = useMemo(() => ([
    { key: 'createdAt', label: 'Registriert' },
    { key: 'username', label: 'Username' },
    { key: 'email', label: 'E-Mail' },
    { key: 'emailVerified', label: 'Verifiziert' }
  ]), []);

  return (
    <div className="admin-users">
      {!canManage && <div className="error">Nur Admins dürfen User verwalten.</div>}

      <div className="toolbar">
        <input
          type="text"
          placeholder="Suchen (Username/E-Mail)"
          value={query}
          onChange={(e) => setQuery(e.target.value)}
        />
        <select value={role} onChange={(e) => setRole(e.target.value)}>
          <option value="all">Alle Rollen</option>
          <option value="user">User</option>
          <option value="moderator">Moderator</option>
          <option value="admin">Admin</option>
        </select>
        <select value={sortBy} onChange={(e) => setSortBy(e.target.value)}>
          {sortedColumns.map(col => (
            <option key={col.key} value={col.key}>{col.label} sortieren</option>
          ))}
        </select>
        <select value={order} onChange={(e) => setOrder(e.target.value)}>
          <option value="asc">Aufsteigend</option>
          <option value="desc">Absteigend</option>
        </select>
        <button onClick={loadUsers} disabled={loading}>Neu laden</button>
      </div>

      {error && <div className="error">{error}</div>}

      <div className="summary">Gesamt: {total}</div>

      {loading ? (
        <div className="loading">Lade Nutzer…</div>
      ) : (
        <table className="users-table">
          <thead>
            <tr>
              <th>Registriert</th>
              <th>Username</th>
              <th>E-Mail</th>
              <th>Rollen</th>
              <th>Verifiziert</th>
              <th>Aktionen</th>
            </tr>
          </thead>
          <tbody>
            {users.map(u => (
              <tr key={u._id}>
                <td>{new Date(u.createdAt).toLocaleString()}</td>
                <td>{u.username}</td>
                <td>{u.email}</td>
                <td>{(u.roles || []).join(', ')}</td>
                <td>{u.emailVerified ? '✅' : '❌'}</td>
                <td className="actions">
                  <button onClick={() => openNotify(u)}>Benachrichtigen</button>
                  <button onClick={() => handleRoleChange(u._id, (u.roles || []).includes('moderator') ? 'user' : 'moderator')}>
                    {(u.roles || []).includes('moderator') ? 'Zu User' : 'Zu Moderator'}
                  </button>
                  <button className="danger" onClick={() => handleDelete(u._id)}>Löschen</button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      )}

      {notifyTarget && (
        <div className="modal">
          <div className="modal-content">
            <h3>Benachrichtigung an {notifyTarget.username}</h3>
            <textarea
              value={notifyMessage}
              onChange={(e) => setNotifyMessage(e.target.value)}
              placeholder="Nachricht eingeben…"
              rows={4}
            />
            <div className="modal-actions">
              <button onClick={() => setNotifyTarget(null)}>Abbrechen</button>
              <button onClick={sendNotify} disabled={!notifyMessage.trim()}>Senden</button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

export default AdminUsersManager;
